import React, { useEffect, useMemo, useState } from 'react';
import { useForm, Controller, FormProvider } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import styles from "./styles.module.scss";
import { CaretDownIcon } from "assets";
import { Input, RadioInput } from "components";
import { BasicExpectationsPayload, HiringAssistantContinuePayload, useBasicExpectations } from 'hooks';
import { normalizeWorkMode, shouldCollectJobLocation } from 'utils/workMode';
import type { SalaryDetails } from 'components/chat/chat-messages/types';

export const appendIdToUrlAsQuery = (id: number | string) => {
  const currentUrl = new URL(window.location.href);
  currentUrl.searchParams.set('id', String(id));
  window.history.pushState({}, '', currentUrl);
};

interface OptionType {
  label: string;
  value: string;
}

export interface Question {
  id: string;
  title: string;
  type: 'radio' | 'checkbox';
  options: OptionType[];
  optionsLayout?: 'list' | 'grid';
  optionDisplayStyle?: 'default' | 'coreResponsibilities';
}

interface OptionsFormProps {
  assessmentId: string | null;
  expectations?: any;
  formData?: Record<string, string | string[]>;
  salaryDetails?: SalaryDetails;
  setIsLoading: (loading: boolean) => void;
  fetchAssessmentSessions: () => void;
  onChange: (questionId: string, value: string | string[]) => void;
  onSalaryChange: (salaryDetails: SalaryDetails) => void;
  className?: string;
  optionStyle?: React.CSSProperties;
  optionContainerClass?: string;
  questions: Question[];
  isSubmitted: boolean;
  historicalFormData?: Record<string, string | string[]>;
  historicalSalaryDetails?: SalaryDetails | null;
  sendRequest: (payload: HiringAssistantContinuePayload) => void;
}

interface FormValues {
  [key: string]: string | string[] | boolean;
  currency: string;
  frequency: string;
  minSalary: string;
  maxSalary: string;
  displaySalary: boolean;
}

type FormSchema = yup.ObjectSchema<Record<string, any>>;

/**
 * Caps on how many options a checkbox question accepts. A question left out of this map is
 * uncapped — which is also what makes the "Select all" shortcut valid for it, so validation
 * and the shortcut stay in step from this one source.
 */
const MAX_SELECTIONS: Record<string, number> = {
  workEnvironment: 2,
};

/** A checkbox question with no cap can be filled in one click. */
export const supportsSelectAll = (question: Question): boolean =>
  question.type === 'checkbox' &&
  MAX_SELECTIONS[question.id] === undefined &&
  question.options.length > 0;

const createValidationSchema = (questions: Question[]): FormSchema => {
  const schemaShape: { [key: string]: any } = {};

  questions.forEach(question => {
    if (question.type === 'radio') {
      schemaShape[question.id] = yup.string().required(`${question.title.split('\n')[0]} is required`);
    } else {
      // Base schema for checkbox (at least 1 selection)
      let schema = yup.array().min(1, `Select at least one option for ${question.title.split('\n')[0]}`);
      const maxSelections = MAX_SELECTIONS[question.id];
      if (maxSelections !== undefined) {
        schema = schema.max(maxSelections, `Maximum selection for ${question.title.split('\n')[0]} is ${maxSelections}`);
      }

      schemaShape[question.id] = schema;
    }
  });

  schemaShape.displaySalary = yup.boolean().default(false);
  schemaShape.address = yup.string().when('workMode', {
    is: (workMode: string) => shouldCollectJobLocation(workMode),
    then: (schema) => schema.trim().required('Job location is required'),
    otherwise: (schema) => schema.optional(),
  });
  schemaShape.currency = yup.string().required('Currency is required');
  schemaShape.frequency = yup.string().required('Frequency is required');
  schemaShape.minSalary = yup
    .string()
    .required('Minimum salary is required')
    .test('positive', 'Minimum salary must be greater than 0', function (value) {
      return Number(value) > 0;
    })
    .test('max-digits', 'Minimum salary must not exceed 30 digits', function (value) {
      if (!value) return true;
      const digitsOnly = value.replace(/[^0-9]/g, '');
      return digitsOnly.length <= 30;
    });
  schemaShape.maxSalary = yup
    .string()
    .required('Maximum salary is required')
    .test('positive', 'Maximum salary must be greater than 0', function (value) {
      return Number(value) > 0;
    })
    .test('max-digits', 'Maximum salary must not exceed 30 digits', function (value) {
      if (!value) return true;
      const digitsOnly = value.replace(/[^0-9]/g, '');
      return digitsOnly.length <= 30;
    })
    .test('is-greater', 'Maximum salary must be greater than minimum salary', function (value) {
      const { minSalary } = this.parent;
      return Number(value) > Number(minSalary);
    });

  return yup.object().shape(schemaShape);
};

const OptionsForm: React.FC<OptionsFormProps> = ({
  assessmentId,
  expectations,
  formData = {},
  salaryDetails = {
    currency: 'USD',
    frequency: 'monthly',
    minSalary: '',
    maxSalary: '',
  },
  setIsLoading,
  fetchAssessmentSessions,
  onChange,
  className = '',
  optionStyle,
  onSalaryChange,
  questions,
  optionContainerClass = '',
  isSubmitted,
  historicalFormData = {},
  historicalSalaryDetails = null,
  sendRequest
}) => {
  const [isSubmittedImmediately, setIsSubmittedImmediately] = useState(false);
  const validationSchema = useMemo(() => createValidationSchema(questions), [questions]);
  const { basicExpectationsResponse, isLoading: basicExpectationsLoading, status, error: basicExpectationsError, submitBasicExpectations } = useBasicExpectations(assessmentId);
  const defaultValues = useMemo(() => {
    const values: { [key: string]: string | string[] } = {};
    questions.forEach((question) => {
      if (question.id === "coreResponsibilities" && historicalFormData[question.id]) {
        const data = historicalFormData[question.id];
        if (Array.isArray(data)) {
          values[question.id] = data.flat().filter((item): item is string => typeof item === "string");
        } else if (typeof data === "string") {
          values[question.id] = [data];
        } else {
          values[question.id] = [];
        }
      } else {
        values[question.id] = historicalFormData[question.id] || (question.type === "radio" ? "" : []);
      }
    });

    values.currency = historicalSalaryDetails?.currency || "USD";
    values.frequency = historicalSalaryDetails?.frequency || "monthly";
    values.minSalary = historicalSalaryDetails?.minSalary || "";
    values.maxSalary = historicalSalaryDetails?.maxSalary || "";
    const expectationSource = expectations?.basic_expectation
      ?? expectations?.expectations
      ?? expectations
      ?? {};
    const suggestedAddress = expectationSource.address?.recommended
      ?? expectationSource.address
      ?? expectationSource.location?.recommended
      ?? expectationSource.location
      ?? '';
    const historicalAddress = historicalFormData.address;
    values.address = (typeof historicalAddress === 'string' ? historicalAddress : '')
      || (typeof suggestedAddress === 'string' ? suggestedAddress : '');
    values.displaySalary = (
      expectations?.display_salary
      ?? expectations?.basic_expectation?.display_salary
      ?? expectations?.expectations?.display_salary
    ) ?? false;

    return values as FormValues;
  }, [questions, historicalFormData, historicalSalaryDetails, expectations]);

  const methods = useForm({
    resolver: yupResolver(validationSchema) as any,
    defaultValues,
    mode: "onChange",
  });

  const { control, handleSubmit, watch, formState: { errors }, register, setValue } = methods;
  const selectedWorkMode = watch('workMode') as string;

  useEffect(() => {
    if (normalizeWorkMode(selectedWorkMode) === 'remote') {
      setValue('address', '', { shouldValidate: true });
    }
  }, [selectedWorkMode, setValue]);

  const handleSelectAll = (question: Question, checked: boolean) => {
    setValue(
      question.id,
      checked ? question.options.map((option) => option.value) : [],
      { shouldValidate: true, shouldDirty: true },
    );
  };

  useEffect(() => {
    const subscription = watch((value, { name, type }) => {
      if (name) {
        const question = questions.find((q) => q.id === name);
        if (question) {
          const fieldValue = value[name] as string | string[];
          onChange(name, fieldValue);
        } else if (name === 'address') {
          onChange(name, typeof value.address === 'string' ? value.address : '');
        } else if (["currency", "frequency", "minSalary", "maxSalary"].includes(name)) {
          onSalaryChange({
            currency: value.currency || "",
            frequency: value.frequency || "",
            minSalary: value.minSalary || "",
            maxSalary: value.maxSalary || "",
          });
        }
      }
    });
    return () => subscription.unsubscribe();
  }, [watch, questions, onChange, onSalaryChange]);

  const onFormSubmit = (data: FormValues) => {
    const expectationsData = expectations?.core_responsibilities || {};
    const coreResponsibilitiesPayload: Record<string, any> = {};
    const selectedResponsibilities = Array.isArray(data.coreResponsibilities)
      ? data.coreResponsibilities
      : [];

    // Counter for sequential numbering in the output
    let counter = 1;

    // Loop through all responsibilities from the expectations data
    let i = 1;
    while (expectationsData[`responsibility${i}`] !== undefined) {
      const responsibilityKey = `responsibility${i}`;
      const descriptionKey = `description${i}`;

      // Get the responsibility value from expectations (handles both flat and nested structures)
      const rawValue = expectationsData[responsibilityKey];
      const responsibilityTitle = typeof rawValue === 'object' && rawValue !== null ? rawValue.title : rawValue;
      const responsibilityDesc = typeof rawValue === 'object' && rawValue !== null ? rawValue.description : expectationsData[descriptionKey];

      // Check if this responsibility was selected by the user
      const isSelected = responsibilityTitle &&
        selectedResponsibilities.includes(responsibilityTitle.toLowerCase().replace(/\s+/g, '_'));

      // Only include selected responsibilities in the payload
      if (isSelected) {
        coreResponsibilitiesPayload[`responsibility${counter}`] = responsibilityTitle;
        coreResponsibilitiesPayload[`check${counter}`] = true;
        coreResponsibilitiesPayload[`description${counter}`] = responsibilityDesc || '';
        counter++;
      }
      i++;
    }

    const employmentType = data.typeOfEmployment as string;
    const experienceLevel = data.experienceLevel as string;
    const yearsOfExperience = data.yearsOfExperience as string;
    const workMode = normalizeWorkMode(data.workMode as string);
    const workEnvironment = Array.isArray(data.workEnvironment)
      ? data.workEnvironment[0]
      : data.workEnvironment as string;

    const basicExpectationsPayload: BasicExpectationsPayload = {
      assessment_id: assessmentId || '',
      employment_type: employmentType,
      experience_level: experienceLevel,
      years_of_experience: yearsOfExperience,
      work_mode: workMode,
      address: shouldCollectJobLocation(workMode)
        ? String(data.address || '').trim()
        : null,
      experience_environment: workEnvironment,
      core_responsibilities: coreResponsibilitiesPayload,
      salary_range_term: (data.frequency as string).toLowerCase(),
      salary_range_currency: data.currency as string,
      min_salary_range_amount: Number(data.minSalary),
      max_salary_range_amount: Number(data.maxSalary),
      require_work_mode:
        expectations?.require_work_mode
        ?? expectations?.basic_expectation?.require_work_mode
        ?? expectations?.expectations?.require_work_mode
        ?? true,
      require_salary_range:
        expectations?.require_salary_range
        ?? expectations?.basic_expectation?.require_salary_range
        ?? expectations?.expectations?.require_salary_range
        ?? true,
      display_salary: Boolean(data.displaySalary),
      // The server defaults this column to false, so every new job came back with
      // the Portfolio Link toggle off even though the whole app treats an absent
      // value as on. Persist the intended default at creation instead, keeping any
      // value the assistant already parsed.
      require_portfolio_upload:
        expectations?.require_portfolio_upload
        ?? expectations?.basic_expectation?.require_portfolio_upload
        ?? expectations?.expectations?.require_portfolio_upload
        ?? true,
    };

    // Submit the data
    submitBasicExpectations(basicExpectationsPayload);
    setIsLoading(true);
    fetchAssessmentSessions();
    appendIdToUrlAsQuery(assessmentId || '');
    setIsSubmittedImmediately(true);
  };

  useEffect(() => {
    if (basicExpectationsError) {
      setIsLoading(false);
      setIsSubmittedImmediately(false); // Allow resubmission after error
    }
  }, [basicExpectationsError])

  useEffect(() => {
    if (status === "success" && !basicExpectationsLoading) {
      sendRequest({
        assessment_id: assessmentId,
        input: "okay"
      });
    }
  }, [basicExpectationsResponse])

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onFormSubmit)} className={`${styles.formContainer} ${className}`}>
        <div className='pt-5 pb-2 mb-5 border-t border-b border-[#D0D5DD]'>
          <h2 className={styles.formTitle}>Get started with Your Basic Requirements</h2>
          <p className={styles.formSubtitle}>
            Let's organize the foundational details for your hiring process. Select the options that best match your needs.
          </p>
        </div>


        {questions.map((question, index) => {
          const [mainQuestion, subQuestion] = question.title.split("\n");
          const selectedValues = question.type === "checkbox"
            ? ((watch(question.id) as string[]) ?? [])
            : [];
          const canSelectAll = supportsSelectAll(question) && !isSubmitted;
          const allSelected = canSelectAll && selectedValues.length === question.options.length;

          return (
            <div key={question.id} className={styles.questionContainer}>
              <div className={styles.questionWrapper}>
                <div className={styles.questionTitleRow}>
                  <h3 className={styles.questionTitle}>
                    {index + 1}. {mainQuestion}
                  </h3>
                  {canSelectAll && (
                    <label className={styles.selectAllControl}>
                      <input
                        type="checkbox"
                        className={styles.selectAllInput}
                        checked={allSelected}
                        onChange={(event) => handleSelectAll(question, event.target.checked)}
                      />
                      <span>Select all</span>
                    </label>
                  )}
                </div>
                {subQuestion && (
                  <p className={styles.questionSubtitle}>{subQuestion}</p>
                )}
              </div>
              <div>
                <RadioInput
                  register={register}
                  name={question.id}
                  validatorMessage={errors[question.id]?.message as string}
                  options={question.options}
                  type={question.type}
                  value={question.type === "radio" ? (watch(question.id) as string) : undefined}
                  checkValue={question.type === "checkbox" ? selectedValues : undefined}
                  parentClassName={`${styles.optionsContainer} ${question.optionsLayout === "grid" ? styles.gridLayout : styles.listLayout
                    } ${question.optionDisplayStyle === "coreResponsibilities" ? styles.coreResponsibilities : ""}`}
                  labelClassName={styles.compactLabel}
                />
                {question.id === 'workMode' && shouldCollectJobLocation(selectedWorkMode) && (
                  <Input
                    label="Job location"
                    name="address"
                    placeholder="Enter job location"
                    register={register}
                    validatorMessage={errors.address?.message as string}
                    className={styles.jobLocationInput}
                    parentClassName={styles.jobLocationField}
                    showRequired
                  />
                )}
              </div>
            </div>
          );
        })}

        <div className={styles.questionContainer}>
          <div className={styles.questionWrapper}>
            <div className={styles.salaryTitleRow}>
              <h3 className={styles.questionTitle}>{questions.length + 1}. Salary Details</h3>
              {!isSubmitted && (
                <div className={styles.salaryToggleArea}>
                  <div className={styles.tooltipWrapper}>
                    <span className={styles.tooltipText}>Turn this on to show the salary range to candidates in the job description</span>
                  </div>
                  <span className={styles.toggleLabelText}>Display Salary</span>
                  <Controller
                    name="displaySalary"
                    control={control}
                    render={({ field }) => (
                      <label className={styles.toggleWrapper}>
                        <input
                          type="checkbox"
                          checked={Boolean(field.value)}
                          onChange={field.onChange}
                          className={styles.toggleInput}
                        />
                        <span className={`${styles.toggleSlider} ${field.value ? styles.active : ''}`}>
                          <span className={styles.toggleButton}></span>
                        </span>
                      </label>
                    )}
                  />
                </div>
              )}
            </div>
            <p className={styles.questionSubtitle}>
              Enter the salary details and range you need from the talent
            </p>
          </div>
          <div className={styles.salaryDetails}>
            <div className={styles.salaryTerms}>
              <label className={styles.salaryLabel}>Salary Terms</label>
              <div className={styles.dropdownGroup}>
                <div className={styles.dropdownWrapper}>
                  <Controller
                    name="currency"
                    control={control}
                    render={({ field }) => (
                      <>
                        <select {...field} className={styles.dropdown}>
                          <option value="USD">USD</option>
                          <option value="NGN">NGN</option>
                          <option value="EUR">EUR</option>
                          <option value="GBP">GBP</option>
                        </select>
                        <CaretDownIcon className={styles.caretDownIcon} />
                      </>
                    )}
                  />
                </div>
                <div className={styles.dropdownWrapper}>
                  <Controller
                    name="frequency"
                    control={control}
                    render={({ field }) => (
                      <>
                        <select {...field} className={styles.dropdown}>
                          <option value="monthly">Monthly</option>
                          <option value="yearly">Yearly</option>
                        </select>
                        <CaretDownIcon className={styles.caretDownIcon} />
                      </>
                    )}
                  />
                </div>
              </div>
              {errors.currency && (
                <small className={styles.message}>{errors.currency?.message}</small>
              )}
              {errors.frequency && (
                <small className={styles.message}>{errors.frequency?.message}</small>
              )}
            </div>
            <div className={styles.salaryRange}>
              <label className={styles.salaryLabel}>Salary Range</label>
              <div className={styles.rangeInputs}>
                <Controller
                  name="minSalary"
                  control={control}
                  render={({ field }) => (
                    <input
                      type="number"
                      placeholder="Enter min amount"
                      {...field}
                      className={`${styles.rangeInput} ${errors.minSalary ? styles.inputError : ""} placeholder:!text-xs`}
                    />
                  )}
                />
                <Controller
                  name="maxSalary"
                  control={control}
                  render={({ field }) => (
                    <input
                      type="number"
                      placeholder="Enter max amount"
                      {...field}
                      className={`${styles.rangeInput} ${errors.maxSalary ? styles.inputError : ""} placeholder:!text-xs`}
                    />
                  )}
                />
              </div>
              {(errors.minSalary || errors.maxSalary) && (
                <small className={`text-red-500 ${styles.message}`}>
                  {errors.minSalary?.message || errors.maxSalary?.message}
                </small>
              )}
            </div>
          </div>
        </div>

        {(!isSubmitted || Object.keys(historicalFormData).length === 0) && !isSubmittedImmediately && (
          <div className={styles.submitButtonContainer}>
            <button type="submit" className={styles.submitButton}>
              OKAY
            </button>
          </div>
        )}
      </form>
    </FormProvider>
  );
};

export { OptionsForm };
